import { runProjectScan } from '../runScan';
import { writeScanArtifacts } from '../artifacts';
import { formatAgentStatus, ERROR } from '../statusLabels';
import type { Gap } from '../../../../src/station/types';

export interface CheckCommandOptions {
  cwd: string;
  json?: boolean;
  quiet?: boolean;
}

const SEVERITY_ICON: Record<Gap['severity'], string> = {
  critical: '\u{1F534}', // red circle
  warning: '\u{1F7E1}',  // yellow circle
  info: '\u{26AA}',      // white circle
};

const SEVERITY_ORDER: Record<Gap['severity'], number> = { critical: 0, warning: 1, info: 2 };

/**
 * `viberaven check` — the terminal/CI twin of the Studio verdict.
 * Runs the full local scan, prints one line per finding, writes the
 * `.viberaven/` artifacts, and exits 1 when launch blockers exist.
 * Fully offline: no login, no network, no telemetry.
 */
export async function runCheckCommand(options: CheckCommandOptions): Promise<number> {
  const result = await runProjectScan({ workspacePath: options.cwd });

  if (!result.ok) {
    console.error(formatAgentStatus(ERROR, result.message));
    return 2;
  }

  const artifact = result.artifact;
  const paths = await writeScanArtifacts({ artifact, cwd: options.cwd });

  if (options.json) {
    console.log(
      JSON.stringify(
        {
          score: artifact.score,
          scoreLabel: artifact.scoreLabel,
          summary: artifact.summary,
          productionCorePercent: artifact.productionCorePercent,
          gaps: artifact.gaps.map((gap) => ({
            id: gap.id,
            severity: gap.severity,
            title: gap.title,
            detail: gap.detail,
          })),
          artifacts: paths,
        },
        null,
        2
      )
    );
    return artifact.gaps.some((g) => g.severity === 'critical') ? 1 : 0;
  }

  const sorted = [...artifact.gaps].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );
  const critical = sorted.filter((g) => g.severity === 'critical').length;
  const warnings = sorted.filter((g) => g.severity === 'warning').length;

  console.log('');
  console.log(`viberaven check \u00b7 ${options.cwd}`);
  console.log('');

  if (sorted.length === 0) {
    console.log('\u2705 No launch blockers found in repo evidence.');
  } else {
    for (const gap of sorted) {
      console.log(`${SEVERITY_ICON[gap.severity]} ${gap.title}  (${gap.id})`);
      if (!options.quiet && gap.detail) {
        const firstLine = gap.detail.split('\n')[0];
        console.log(`   ${firstLine}`);
      }
    }
  }

  console.log('');
  console.log(
    critical > 0
      ? `Verdict: \u274C ${critical} blocker${critical === 1 ? '' : 's'}, ${warnings} warning${warnings === 1 ? '' : 's'} \u00b7 score ${artifact.score}`
      : `Verdict: \u2705 no blockers, ${warnings} warning${warnings === 1 ? '' : 's'} \u00b7 score ${artifact.score}`
  );
  console.log(`Fix: viberaven fix \u00b7 Details: ${paths.tasklistPath}`);
  console.log('');

  return critical > 0 ? 1 : 0;
}
